import React from "react";
import { connect } from "react-redux";
import { Button, Row } from "react-bootstrap";
import { IKeywords, IWorkflowItemStep } from "../../../../common/sharedTypes";
import { getTableValuesByGroupName } from "../../../../services/Utils/IndexedDBUtils";
import {
  AgencyInfo,
  ErrorInfo,
  initialErrorState,
  initialKeywordValues,
  ReportTypesInfo
} from "../business-objects/common";
import {
  handleInvolvedPartyChange,
  hasValue,
  validateAgency,
  validateDateOfCrash,
  validateNarrativeDiagram
} from "../common/utils";
import KeywordsForm from "../presentation/KeywordsForm";
import {
  getImage,
  getReportTypes
} from "../../../WorkflowQueues/Keywords/redux-objects/actions";
import { getAllAgencies } from "../../../ThirdPartyVendors/redux-objects/actions";
import * as urls from "../../../shared/urls";
import Api from "../../../../services/Api";
import { toast } from "react-toastify";

interface IManagementKeywordsControllerReduxProps {
  agencies: AgencyInfo[];
  reportTypes: ReportTypesInfo[];
  openWorkflowItemStep: IWorkflowItemStep;
  image: Uint8Array;
  getImage: (reportId: number, documentId?: string) => Promise<void>;
  getReportTypes: (url: string) => Promise<void>;
  getAllAgencies: (url: string) => Promise<void>;
}

interface IManagementKeywordsControllerOwnProps {
  keywords: IKeywords;
  canEditReportType: boolean;
}

interface IManagementKeywordsControllerState {
  errors: ErrorInfo;
  keywords: IKeywords;
  counties: any[];
  componentReady: boolean;
}

type IManagementKeywordsControllerProps =
  IManagementKeywordsControllerOwnProps &
  IManagementKeywordsControllerReduxProps;

class ManagementKeywordsController extends React.Component<
  IManagementKeywordsControllerProps,
  IManagementKeywordsControllerState
> {
  constructor(props: IManagementKeywordsControllerProps) {
    super(props);
    const { keywords } = props;
    this.state = {
      errors: initialErrorState,
      keywords: keywords ? keywords : initialKeywordValues,
      counties: [],
      componentReady: false
    };
  }

  componentDidMount() {
    const { componentReady } = this.state;

    if (componentReady) return;

    this.loadData().then(() => {
      this.setState({
        componentReady: true
      });
    });
  }

  loadData = async () => {
    const { keywords, reportTypes, agencies, openWorkflowItemStep } =
      this.props;
    const { counties } = this.state;

    let promises = [];

    if (counties.length === 0) {
      promises.push(
        getTableValuesByGroupName("County").then((results) => {
          this.setState({
            counties: results
          });
        })
      );
    }

    if (openWorkflowItemStep?.workflowItem?.reportId > 0) {
      promises.push(this.props.getImage(openWorkflowItemStep.workflowItem.reportId, openWorkflowItemStep.workflowItem.report?.keywords?.documentId));
    }

    if (reportTypes.length === 0) {
      promises.push(this.props.getReportTypes(urls.GET_ALL_REPORT_TYPES));
    }

    if (agencies.length === 0) {
      promises.push(this.props.getAllAgencies(urls.GET_ALL_AGENCIES));
    }

    await Promise.all(promises);
  };

  setError = (error: { [key: string]: string }) => {
    this.setState((state) => ({
      errors: {
        ...state.errors,
        error
      }
    }));
  };

  validateDateOfCrash = () => {
    const { keywords } = this.state;
    const hasValidDateOfCrash = validateDateOfCrash(
      keywords.dateOfCrash as string
    );

    this.setError({
      crashDate: hasValidDateOfCrash.message
    });

    return hasValidDateOfCrash.isValid;
  };

  validateCountySelection = () => {
    const { keywords } = this.state;
    const hasValidCounty = hasValue(keywords.countyId);

    this.setError({
      countySelection: !hasValidCounty
        ? "A valid county selection is required!"
        : ""
    });

    return hasValidCounty;
  };

  validateInjuryStatusSelection = () => {
    const { keywords } = this.state;
    const hasValidInjuryStatus = hasValue(keywords.injuryStatus);

    this.setError({
      injurySelection: !hasValidInjuryStatus
        ? "A valid injury status selection is required!"
        : ""
    });

    return hasValidInjuryStatus;
  };

  validateAgencySelection = () => {
    const { keywords } = this.state;
    const hasValidAgency = validateAgency(keywords);

    this.setError({
      agencySelection: !hasValidAgency
        ? "A valid agency selection is required!"
        : ""
    });

    return hasValidAgency;
  };

  validateNarrativeDiagram = () => {
    const { keywords } = this.state;
    const hasValidNarrativeDiagram = validateNarrativeDiagram(keywords);

    this.setError({
      narrativeDiagram: !hasValidNarrativeDiagram
        ? "Must specify narrative and diagram pages for Investigator Report!"
        : ""
    });

    return hasValidNarrativeDiagram;
  };

  handleDateChange = (date) => {
    this.setState(
      (state) => ({
        keywords: {
          ...state.keywords,
          dateOfCrash: date
        }
      }),
      () => {
        this.validateDateOfCrash();
      }
    );
  };

  handleInvolvedPartyChange = (event) => {
    const { keywords } = this.state;
    const newParties = handleInvolvedPartyChange(event, keywords);

    this.setState((state) => ({
      keywords: {
        ...state.keywords,
        involvedParties: newParties
      }
    }));
  };

  handleChange = (event) => {
    const { name, value, type } = event.target;
    if (type !== "manual") event.preventDefault();

    this.setState(
      (state) => ({
        keywords: {
          ...state.keywords,
          [name]: value
        }
      }),
      () => {
        switch (name) {
          case "countyId":
            this.validateCountySelection();
          case "injuryStatus":
            this.validateInjuryStatusSelection();
          case "agencyId":
            this.validateAgencySelection();
          case "narrativeDiagramPages":
            this.validateNarrativeDiagram();
        }
      }
    );
  };

  handleSave = async () => {
    const response = await Api.post<IKeywords>(
      urls.UPDATE_ADD_EDIT_KEYWORDS_DATA,
      {
        keywords: this.state.keywords
      }
    );

    if (!response.ok) {
      toast.error("Failed to update keywords");
    } else {
      this.setState(
        {
          keywords: response.data
        },
        () => toast.success("Keywords updated")
      );
    }
  };

  render() {
    return (
      <>
        {this.state.componentReady ? (
          <>
            <KeywordsForm
              agencies={this.props.agencies}
              counties={this.state.counties}
              reportTypes={this.props.reportTypes}
              errors={this.state.errors}
              keywords={this.state.keywords}
              image={this.props.image}
              splitCaseMode={true} // for hiding header
              reportId={this.props.openWorkflowItemStep?.workflowItem?.reportId}
              showNotificationTemplateSelector={false}
              onInvolvedPartyBlur={null}
              onInvolvedPartyChange={this.handleInvolvedPartyChange}
              onChange={this.handleChange}
              onDateChange={this.handleDateChange}
              canEditReportType={this.props.canEditReportType}
            />
            <Row className="px-4 pt-4">
              <Button className="mr-2" onClick={() => this.handleSave()}>
                Save
              </Button>
            </Row>
          </>
        ) : (
          <p>Loading...</p>
        )}
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  agencies: state.Vendors.allAgencies,
  counties: state.Keywords.counties,
  reportTypes: state.Keywords.reportTypes,
  openWorkflowItemStep: state.Keywords.openWorkflowItemStep,
  image: state.Keywords.image
});

const mapDispatchToProps = {
  getImage,
  getReportTypes,
  getAllAgencies
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ManagementKeywordsController);
